import type { ReplicaDatabase } from '../db/replica-database';
import type { HttpClient } from '../http/http-client';
import type { FileRef } from '../types/file-ref';
import { sha256Hex } from './sha256-hex';

const TRANSFER_TIMEOUT_MS = 5 * 60_000;

/**
 * File bytes are kept on every device by sha256, so documents open offline. New files are stored
 * locally first and uploaded on the next sync; files added elsewhere are downloaded after a pull.
 */
export class FileStore {
  constructor(
    private readonly db: ReplicaDatabase,
    private readonly http: HttpClient,
    readonly maxBytes: number,
  ) {}

  async add(file: File): Promise<FileRef> {
    if (file.size > this.maxBytes) throw new RangeError('file.too_large');
    const sha256 = await sha256Hex(file);
    const type = file.type || 'application/octet-stream';
    if (!(await this.db.blobs.get(sha256))) {
      await this.db.blobs.put({
        sha256,
        blob: file,
        name: file.name,
        type,
        size: file.size,
        uploaded: 0,
        added_at: new Date().toISOString(),
      });
    }
    return { sha256, name: file.name.slice(0, 200) || 'file', type, size: file.size };
  }

  async uploadPending(): Promise<number> {
    const pending = await this.db.blobs.where('uploaded').equals(0).toArray();
    for (const blob of pending) {
      await this.http.request(`/files/${blob.sha256}`, {
        method: 'PUT',
        body: blob.blob,
        headers: { 'Content-Type': blob.type },
        timeoutMs: TRANSFER_TIMEOUT_MS,
      });
      await this.db.blobs.update(blob.sha256, { uploaded: 1 });
    }
    return pending.length;
  }

  /** Every file referenced by a live record (`data.attachments`), deduplicated. */
  async referenced(): Promise<Map<string, FileRef>> {
    const found = new Map<string, FileRef>();
    for (const record of await this.db.records.toArray()) {
      if (record.deleted_at) continue;
      const list = record.data.attachments;
      if (!Array.isArray(list)) continue;
      for (const ref of list as FileRef[]) found.set(ref.sha256, ref);
    }
    return found;
  }

  async downloadMissing(): Promise<number> {
    const have = new Set(await this.db.blobs.toCollection().primaryKeys());
    let count = 0;
    for (const [sha256, ref] of await this.referenced()) {
      if (have.has(sha256)) continue;
      const response = await this.http.request(`/files/${sha256}`, { timeoutMs: TRANSFER_TIMEOUT_MS });
      const blob = await response.blob();
      if ((await sha256Hex(blob)) !== sha256) continue; // corrupted in transit: retried next sync
      await this.db.blobs.put({
        sha256,
        blob,
        name: ref.name,
        type: ref.type,
        size: blob.size,
        uploaded: 1,
        added_at: new Date().toISOString(),
      });
      count++;
    }
    return count;
  }
}
